"use strict";
/* © 2014 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
	* Unauthorized copying of this file, via any medium is strictly prohibited
	* Proprietary and confidential
	* Written by Aetonix, June 19, 2014
	* For information or permission request, email info@aetonixsystems.com
	*/
var Promise = require("promise6");
var par = require("par");
var prop = require("prop");
var asap = require("asap");
var window = require("global");
var debounce = require("debounce");

var makeCall = require("./call");
var getRTC = require("./get_adapter");

var ring_time = 2 * 60 * 1000; // 2 minutes to ring
var busy_time = 4 * 1000; // 4 seconds of busy signal
var CONNECT_TIME = 20 * 1000; // 20 seconds to go from connecting to connected
var RECALCULATE_TIME = 500; // half a second delay when recalculating the resolution
var RENEGOTIATE_DELAY = 2 * 1000;

exports.createBuzz = createBuzz;

/**
	* This is used to create a new instance of Buzz.
	* @param  {API}        api        An instance of ae-api bound to an existing user
	* @param  {Dispatcher} dispatcher An instance of ae-dispatcher to connect to events
	* @param  {Object}     config     A configuration object that contains the fname, lname, and _id of the current user
	* @return {Buzz}                  Returns a new instance of Buzz
	*/
function createBuzz(api, dispatcher, config) {
	var RTC = getRTC();

	var Buzz = {
		RTC: RTC,

		// Methods for intantiating a call
		call: make_call,
		accept: accept_call,
		deny: deny_call,
		hangup: hangup_call,

		// Actions to take during a call
		conference: make_conference,
		redirect: redirect_call,
		renegotiate: renegotiate_call,

		// Utility methods
		busy: go_busy,
		setState: setState,

		// BOOM
		clean: clean,
		destroy: destroy,

		// Exposed state
		who: "",
		room: "",
		state: "idle"
	};

	// Tracker used for tracking events related to buzz directly
	var tracker = dispatcher.createTracker();

	// Used for tracking the current call instance and conference call instance
	var current_call = null;
	var conference_call = null;

	// Reference to timer that's being used to time out a call
	var call_timer = null;

	var first_connection = true;

	var ignoreBusy = true;

	// Adds listeners to the dispatcher that are required for Buzz
	init_listeners();

	// Return object that represents the Buzz instance. Everything after this is named functions so they get hoisted
	return Buzz;

	/**
		* Adds listeners to the dispatcher for triggering various actions in Buzz
		*/
	function init_listeners() {
		tracker.on("mqtt:$/call", handle_call);
		tracker.on("mqtt:$/call/conference", handle_conference_call);

		tracker.on("buzz:trigger:call", make_call);
		tracker.on("buzz:trigger:silent", make_silent);
		tracker.on("buzz:trigger:accept", accept_call);
		tracker.on("buzz:trigger:deny", deny_call);
		tracker.on("buzz:trigger:hangup", hangup_call);

		tracker.on("buzz:trigger:conference", make_conference);
		tracker.on("buzz:trigger:redirect", redirect_call);
		tracker.on("buzz:trigger:renegotiate", debounce(renegotiate_call, RENEGOTIATE_DELAY, true));
		tracker.on("buzz:trigger:switch", switch_camera);

		tracker.on("buzz:trigger:busy", go_busy);
		tracker.on("buzz:trigger:idle", go_idle);

		tracker.on("buzz:trigger:video:show", show_video_view);
		tracker.on("buzz:trigger:video:hide", hide_video_view);
		tracker.on("buzz:trigger:video:recalculate", recalculate_video_view);
	}

	/**
		* Creates a new call going out to somebody
		* @param  {Object}  data Should contain `who` the call is for
		* @return {Promise}      Resolves if the call was created and sent successfully
		*/
	function make_call(data) {
		var who = data.who;
		var reason = data.reason;
		var voiceOnly = !!data.voiceOnly;
		var metadata = data.metadata;

		first_connection = true;
		ignoreBusy = data.ignoreBusy;

		if (Buzz.state !== "idle")
			return Promise.reject(new Error("Already in a call"));

		setState("calling");

		return api.calls
			.call(who, reason, voiceOnly, metadata)
			.then(prop("room"))
			.then(par(create_outgoing_call, who, voiceOnly, metadata))
			.catch(handle_call_fail)
			.then(par(logger, "L", "Making call to " + who))
			.then(par(api.calls.read, who));
	}

	function make_silent(data) {
		var who = data.who;
		var reason = data.reason;
		Buzz.who = who;

		if (Buzz.state !== "idle")
			return Promise.reject(new Error("Already in a call"));

		go_busy();
		emit("silent:notify", {
			who: who
		});

		return api.calls
			.silent(who, reason)
			.catch(handle_call_fail)
			.then(par(api.calls.notifymissed))
			.then(par(logger, "L", "Making call to " + who));
	}

	/**
		* Creates a new conference call inviting somebody to the current one
		* This can only be called if there is already a connected call in progress
		* @param  {Object}  data Should contain `who` should be added via conference call
		* @return {Promise}      Resolves once the call was created and delivered
		*/
	function make_conference(data) {
		var state = Buzz.state;

		var other = Buzz.who;
		var who = data.who;

		var voiceOnly = !!data.voiceOnly;

		if (state !== "connected")
			return Promise.reject(new Error("Not currently in a call"));

		if (conference_call)
			return Promise.reject(new Error("Already have a conference call"));

		return api.calls
			.conference(who, other, voiceOnly)
			.then(function (callData) {
				var room = callData.room;
				return current_call.conference(who, room, voiceOnly)
					.then(par(notify_conference, who, other, room, voiceOnly));
			});
	}

	/**
		* Redirects the opposite side of the current call to another person
		* @param  {Object}  data Contains who the person should be reidrected `to`
		* @return {Promise}      Resolves once the redirection event was sent
		*/
	function redirect_call(data) {
		if (Buzz.state !== "connected")
			return Promise.reject(new Error("Not currently in a call"));

		if (conference_call)
			return Promise.reject(new Error("Cannot redirect while in a conference call"));

		return current_call.redirect(data.to);
	}

	/**
		* Tells ohter parties in the current call to renegotiate their RTC connections
		* @param  {Object} data Can contain a `reason` for the renegotiation
		* @return {Promise}      Resolves once the renegotiation notification gets sent
		*/
	function renegotiate_call(data) {
		if (Buzz.state !== "connected")
			return Promise.reject(new Error("Not currently in a call"));

		var reason = data.reason;

		var results = [
			current_call.renegotiate(reason)
		];

		if (conference_call && !conference_call.isSameRoom(current_call))
			results.push(conference_call.renegotiate(reason));

		return Promise.all(results);
	}

	/**
		* Accepts the current incoming call
		* @return {Promise} Resolves when the call has been accepted
		*/
	function accept_call() {
		if (conference_call)
			conference_call.accept();
		if (current_call)
			return current_call.accept()
				.then(par(logger, "L", "Accepting Call"))
				.then(par(api.calls.read, Buzz.who));
		return Promise.reject(new Error("No current call"));
	}

	/**
		* Denies the current incoming call. Optionally takes a reason for denying
		* @param  {Object}  data Can optionally contain a `reason`
		* @return {Promise}      Resolves once the call has been denied
		*/
	function deny_call(data) {
		if (current_call)
			return current_call.deny((data || {}).reason)
				.then(par(logger, "L", "Denying Call"));
		return Promise.reject(new Error("No current call"));
	}

	/**
		* Hangs up the current call. Also notifies of a missed call if the call was still in the "calling" state.
		* @param  {Object}  data Can optionally contain a `reason`
		* @return {Promise}      Resolves once the call has been hung up
		*/
	function hangup_call(data) {
		var state = Buzz.state;
		var who = Buzz.who;
		var reason = (data || {}).reason;

		if (conference_call)
			conference_call.hangup(reason);

		if (current_call)
			return current_call
				.hangup(reason)
				.then(function () {
					if (state === "calling") notify_missed(who);
					logger("L", "Hanging up Call");
				});
		return Promise.reject(new Error("No current call"));
	}

	/**
		* Go to the busy state for a few seconds. Destroys the current call if there is one
		* @return {Promise} Resolves once the busy state has been reached
		*/
	function go_busy() {
		var first = Promise.resolve();
		if (current_call)
			first = current_call.destroy(true)
				.then(par(notify_missed, Buzz.who));
		if (conference_call)
			conference_call.destroy(true);

		var startBusy = start_busy_state;
		if(ignoreBusy) {
			startBusy = function(){};
			ignoreBusy = false;
		}

		return first.then(startBusy);
	}

	function start_busy_state() {
		setTimeout(function () {
			setState("busy");
			logger("L", "Busy");
			start_busy_timer();
		}, 200);
	}

	/**
		* Forces the idle state by clearing the current call if one exists
		*/
	function go_idle() {
		if (current_call) current_call.hangup("forced");
		if (conference_call) conference_call.destroy(true);
		// Go to the idle state regardless of what the calls were doing
		setState("idle");
	}

	/**
		* Cleans up the state of Buzz of stuff to do with the calls.
		* Destroys existing calls if they exist
		*/
	function clean() {
		if (current_call) current_call.destroy();
		if (conference_call) conference_call.destroy();

		current_call = null;
		conference_call = null;
		Buzz.who = null;
		Buzz.room = null;
		logger("L", "Cleaning up");
	}

	/**
		* Destroys Buzz. Cleans up any calls and event trackers
		* @return {Promise} Resolves once destroyed
		*/
	function destroy() {
		if (Buzz.state === "destroyed")
			return Promise.reject(new Error("Already destroyed"));
		clean();
		if (tracker) {
			tracker.off();
			tracker = null;
		}
		Buzz.state = "destroyed";
		return Promise.resolve();
	}

	/**
		* Event handler for when a new call comes in through MQTT.
		* Does some state checks and either notifies the user of the call,
		* switches the current call if calling that user already,
		* or auto-denies the call if busy
		* @param  {Object} data Should contain a `room` and who the call is `from`
		*/
	function handle_call(data) {
		var who = data.from;
		var room = data.room;
		var state = Buzz.state;
		var voiceOnly = !!data.voiceOnly;
		var metadata = data.metadata;

		first_connection = true;

		// Avoid duplicate messages
		if (Buzz.room === room) return;

		if (state === "idle")
			return create_incoming_call(who, room, voiceOnly, metadata);
		if (should_submit(who)) return switch_calls(who, room, voiceOnly);
		else create_call(who, false, room, voiceOnly)
			.then(function (call) {
				call.deny("Busy");
			});
	}

	/**
		* Event handler for when a new conference call comes in through MQTT
		* Notifies the user of the call and sets up for conference calling.
		* If the user is not idle, the call gets automatically denied
		* @param  {Object} data Should contain the `room` and `participants`
		*/
	function handle_conference_call(data) {
		var room = data.room;
		var participants = data.participants;
		var voiceOnly = !!data.voiceOnly;

		var inviter = participants[0];
		var other = participants[1];

		var state = Buzz.state;

		if (state === "idle") return Promise.all([
			create_incoming_conference_call(other, room, false, voiceOnly),
			create_incoming_conference_call(inviter, room, true, voiceOnly)
		]).then(function () {
			emit("call:incoming", {
				from: inviter,
				room: room,
				voiceOnly: voiceOnly,
				conference: other
			});
			emit("conference", {
				who: other
			});
		}).catch(makeError("Couldn't create conference calls"));
		else return create_call(inviter, false, room, voiceOnly)
			.then(function (call) {
				call.deny("Busy");
			});
	}

	/**
		* Sets the state of Buzz. Notifying via the dispatcher.
		* Doesn't change if already in that state
		* @param {String} state The new state to go to
		*/
	function setState(state) {
		clearTimeout(call_timer);
		if (Buzz.state === state) return state;
		Buzz.state = state;
		emit("statechange", state);
		emit("state:" + state);
		logger("D", "State: " + state);
	}

	/**
		* Emits an event to the dispatcher prefixed by `buzz:`
		* @param {String} event The event to emit
		* @param {Object} data  The value to emit
		*/
	function emit(event, data) {
		asap(function () {
			dispatcher.emit("buzz:" + event, data);
		});
	}

	/**
		* Creates a new outgoing call instance, sets up listeners and notifies via dispatcher
		* @param  {String}  who  Who the call is going to
		* @param  {String}  room The room the call should be listening on
		* @return {Promise}      Resolves once the call was created
		*/
	function create_outgoing_call(who, voiceOnly, metadata, room) {
		Buzz.who = who;
		Buzz.room = room;

		return create_call(who, true, room, voiceOnly)
			.then(set_current_call)
			.then(listen_call_events)
			.then(pass_trough(start_call_timer))
			.then(par(notify_call, {
				who: who,
				room: room,
				voiceOnly: voiceOnly,
				metadata: metadata
			}))
			.then(par(emit, "call:outgoing"));
	}

	/**
		* Creates a new incoming call, sets up listeners and notifies via dispatcher
		* @param  {String}  who        Who the call is coming from
		* @param  {String}  room       The room the call is coming from
		* @return {Promise}            Resolves once the call was created
		*/
	function create_incoming_call(who, room, voiceOnly, metadata) {
		var call_data = {
			from: who,
			room: room,
			voiceOnly: voiceOnly,
			metadata: metadata
		};

		Buzz.who = who;
		Buzz.room = room;

		return create_call(who, false, room, voiceOnly)
			.then(set_current_call)
			.then(listen_call_events)
			.then(par(setState, "receiving"))
			.then(pass_trough(start_call_timer))
			.then(function (call) {
				emit("call:incoming", call_data);
				return call;
			})
			.catch(makeError("Error handling incoming call"));
	}

	/**
		* Creates a conference call instance.
		* This is used when a room notifies everyone of a conference call.
		* @param  {String}  who       The id of the person to connect to via conference call
		* @param  {String}  room      The room to use for signaling for the conference call
		* @return {Promise}           Resolves once the call is set up
		*/
	function create_conference_call(who, room, voiceOnly) {
		var own_id = config.id;

		var self = own_id + "-" + who;
		var other = who + "-" + own_id;

		return create_raw_call(true, room, self, other, voiceOnly, true)
			.then(set_conference_call)
			.then(listen_conference_call_events)
			.then(pass_trough(start_conference_timer));
	}

	/**
		* Create an incoming conference call instance.
		* Conditionally adds events based on whether this is the "main" caller or not
		* @param  {String}  who  The id of the person to connect with
		* @param  {String}  room The room used for signaling
		* @param  {Boolean} main Wheter this is the main caller or not
		* @return {Promise}      Resovles once the call is set up
		*/
	function create_incoming_conference_call(who, room, main, voiceOnly) {
		var own_id = config.id;

		var self = own_id + "-" + who;
		var other = who + "-" + own_id;

		var set_call, listen_events, timer;

		if (main) {
			set_call = set_current_call;
			listen_events = listen_call_events;
			timer = start_call_timer;
		} else {
			set_call = set_conference_call;
			listen_events = listen_conference_call_events;
			timer = start_conference_timer;
		}

		return create_raw_call(false, room, self, other, voiceOnly, !main)
			.then(set_call)
			.then(listen_events)
			.then(pass_trough(timer));
	}

	/**
		* Creates a one-to-one call instance
		* @param  {String}  who       Who the call is with
		* @param  {Boolean} initiator Whether the current user is initiating it
		* @param  {String}  room      The room to use for signaling
		* @return {Promise}           Resolves once the call is set up
		*/
	function create_call(who, initiator, room, voiceOnly) {
		var self, other;

		if (initiator) {
			self = "caller";
			other = "callee";
		} else {
			self = "callee";
			other = "caller";
		}

		return create_raw_call(initiator, room, self, other, voiceOnly);
	}

	/**
		* Creates a new call instance using raw paramters
		* @param  {Boolean} initiator Whether the current user is initiating the call
		* @param  {String}  room      The room to use for signaling
		* @param  {String}  self      The prefix for sending own events in the room
		* @param  {String}  other     The prefix for receiving events from the person that should be connected with
		* @return {Promise}           Resolves to the new call instance
		*/
	function create_raw_call(initiator, room, self, other, voiceOnly, locked) {
		var prefix = "mqtt:r/" + room;

		return makeCall({
			dispatcher: dispatcher,
			RTC: RTC,
			initiator: initiator,
			prefix: prefix,
			self: self,
			other: other,
			skipIce: config.skipIce,
			voiceOnly: voiceOnly,
			logger: logger,
			locked: !!locked,
			host: config.host
		});
	}

	/**
		* Listen for call events for the main call
		* @param  {Call} call The call instance to listen on
		* @return {Call}      Returns the call for chaining
		*/
	function listen_call_events(call) {
		call.on("accept", handle_call_accept);
		call.on("deny", handle_call_deny);
		call.on("hangup", handle_call_hangup);

		call.on("stream", handle_call_stream);
		call.on("state", handle_call_state);
		call.on("conference", handle_call_conference);
		call.on("redirect", handle_call_redirect);
		call.on("renegotiating", handle_call_renegotiating);

		call.on("error", handle_call_error);
		call.on("close", handle_call_close);

		return call;
	}

	/**
		* Listens for call events from conference call instances
		* @param  {Call} call The call instance to listen on
		* @return {Call}      Returns the call for chaining
		*/
	function listen_conference_call_events(call) {
		call.on("accept", handle_conference_call_accept);
		call.on("deny", handle_conference_call_deny);
		call.on("hangup", handle_conference_call_hangup);

		call.on("stream", handle_conference_call_stream);
		call.on("state", handle_conference_call_state);
		call.on("renegotiating", handle_conference_renegotiate);

		call.on("error", handle_conference_call_error);
		call.on("close", handle_conference_call_close);

		return call;
	}

	/**
		* Sets the current call reference
		* @param {Call} call The call to save as the current call
		*/
	function set_current_call(call) {
		if (current_call)
			current_call.destroy(true);
		current_call = call;

		setup_unlock();

		return call;
	}

	/**
		* Sets the current conference call reference
		* @param {Call} call The call to save as the current conference call
		*/
	function set_conference_call(call) {
		if (conference_call)
			conference_call.destroy(true);
		conference_call = call;

		if (current_call && (current_call.state === "connected"))
			conference_call.unlock();

		return call;
	}

	function setup_unlock() {
		if (!current_call) return;
		current_call.on("state", function (state) {
			if (state === "connected" && conference_call)
				conference_call.unlock();
		});
	}

	/**
		* Handles and re-emits the accept event of a call
		*/
	function handle_call_accept() {
		emit("accept");
	}

	/**
		* Handles calls going busy. Emits `buzz:busy` with a reason if there is one, goes busy
		* @param  {String} reason The reason for denying the call
		*/
	function handle_call_deny(reason) {
		emit("deny", {
			reason: reason
		});
		go_busy();
	}

	/**
		* Handles call hangups, re-emits with a reason if there is one
		* @param  {String} reason The reason for hanging up
		*/
	function handle_call_hangup(reason) {
		emit("hangup", {
			reason: reason
		});
	}

	/**
		* Handles call streams becoming available, emits `buzz:stream`
		* @param  {Object} stream Contains `remote` and `local` stream uri, or nothing if in phonertc
		*/
	function handle_call_stream(stream) {
		emit("stream", stream);
	}

	/**
		* Handles changes in the call stae. Sets the buzz state if it is valid
		* @param  {String} state The state of the call
		*/
	function handle_call_state(state) {
		if (["started", "closed"].indexOf(state) >= 0) return;
		setState(state);
		if (state === "connecting")
			start_connect_timer();
	}

	/**
		* Handles conference call notifications from a call
		* @param  {Object} data Should contain `who` and `room`
		*/
	function handle_call_conference(data) {
		var who = data.who;
		var room = data.room;
		var voiceOnly = !!data.voiceOnly;

		create_conference_call(who, room, voiceOnly)
			.then(par(emit, "conference", {
				who: who
			}))
			.then(par(api.calls.read, who));
	}

	/**
		* Handles notifications that the other end of the call attempted to redirect the current person
		* @param  {Object} redirection Data about redirection, contains `to`
		*/
	function handle_call_redirect(redirection) {
		emit("redirect", redirection);
	}

	/**
		* Handles notifications that the current call is renegotiating its connection
		* @param  {Object} data Data about renegotation, may contain a `reason`
		*/
	function handle_call_renegotiating(data) {
		emit("renegotiating", data);
	}

	/**
		* Handles and re-emits errors in the call
		* @param  {Error} error The error that occured in the call
		*/
	function handle_call_error(error) {
		makeError("Call Error:")(error);
	}

	/**
		* Handles the call closing, cleans state and emits `buzz:close`
		*/
	function handle_call_close() {
		logger("L", "Disconnected");
		clean();

		if (Buzz.state !== "offline")
			setState("idle");

		emit("close");
	}

	/**
		* Handles the conference call being accepted. Re-emits event
		*/
	function handle_conference_call_accept() {
		emit("conference:accept");
	}

	/**
		* Handles the conference call being denied, re-emits the event with a reason
		* @param  {String} reason The reason the call was denied
		*/
	function handle_conference_call_deny(reason) {
		emit("conference:deny", {
			reason: reason
		});
	}

	/**
		* Handles the conference call being hung up.
		* Cleans the state and re-emits the event with the reason.
		* @param  {String} reason The reason the call was hung up
		*/
	function handle_conference_call_hangup(reason) {
		if (conference_call.successful) {
			current_call.hangup(reason);
		} else conference_call = null;
		emit("conference:hangup", {
			reason: reason
		});
	}

	/**
		* Handles the conference call stream becoming available. re-emits the event
		* @param  {Object} stream Contains `remote` and `local` stream uri, or nothing if in phonertc
		*/
	function handle_conference_call_stream(stream) {
		emit("conference:stream", stream);
		setTimeout(recalculate_video_view, RECALCULATE_TIME);
	}

	/**
		* Handles the conference call changing state. Re-emits the state change
		* @param  {String} state The state of the conference call
		*/
	function handle_conference_call_state(state) {
		emit("conference:statechange", state);
		emit("conference:state:" + state);
		if (state === "connecting")
			start_conference_connect_timer();
	}

	function handle_conference_renegotiate(data) {
		emit("renegotiating", data);
		setTimeout(function () {
			if (current_call && conference_call && (current_call.state === "connected"))
				conference_call.unlock();
		}, 2000);
	}

	/**
		* Handles errors during the conference call. Re-emits the error
		* @param  {Error} error The error that occured
		*/
	function handle_conference_call_error(error) {
		makeError("Conference Call Error:")(error);

	}

	/**
		* Handles the conference call closing. Re-emits the event
		*/
	function handle_conference_call_close() {
		conference_call = null;
		logger("L", "Disconnected");
		emit("conference:close");
	}

	/**
		* Notifies a peson of a call to them
		* @param  {Object} data Should contain `who` and `room`
		* @return {Object}      returns inputted data for chaining
		*/
	function notify_call(data) {
		var room = data.room;
		var who = data.who;
		var voiceOnly = !!data.voiceOnly;
		var metadata = data.metadata;

		dispatcher.emit("$mqtt:u/" + who + "/call", {
			from: config.id,
			room: room,
			voiceOnly: voiceOnly,
			metadata: metadata
		});

		return data;
	}

	/**
		* Notifies a person that they missed a call
		* @param  {String} who The id of who missed a call
		*/
	function notify_missed(who) {
		dispatcher.emit("$mqtt:u/" + who + "/missed_call", {
			from: config.id
		});
	}

	/**
		* Notifies a person that they are being called to a conference call
		* @param  {String} who   Who to call
		* @param  {String} other The other party in the call
		* @param  {String} room  The room the call will use for signaling
		*/
	function notify_conference(who, other, room, voiceOnly) {
		dispatcher.emit("$mqtt:u/" + who + "/call/conference", {
			room: room,
			participants: [config.id, other],
			voiceOnly: voiceOnly
		});
	}

	/**
		* Checks if the curent user should submit to another for calls. (AKA "mine is bigger than yours")
		* @param  {String} who Who to compare to
		*/
	function should_submit(who) {
		var isCalling = Buzz.state === "calling";
		var isReceiving = Buzz.state === "receiving";
		var isSamePerson = Buzz.who === who;
		var isBiggerThanMe = config.id < who;
		if (!isSamePerson) return false;
		if (isReceiving)
			return true;
		return isCalling && isBiggerThanMe;
	}

	/**
		* Switches the current call with a person to receive another one
		* @param  {String}  who  The person making the other call
		* @param  {String}  room The room for the other call
		* @return {Promise}      Resolves once the other call has been made
		*/
	function switch_calls(who, room, voiceOnly) {
		current_call.removeAllListeners();
		return current_call.destroy(true)
			.then(par(create_incoming_call, who, room, voiceOnly));
	}

	/**
		* Starts the timer for going back to idle after being busy
		*/
	function start_busy_timer() {
		setTimeout(un_busy, busy_time);
	}

	/**
		* Goes back to the idle state if currently busy
		* @return {[type]} [description]
		*/
	function un_busy() {
		if (Buzz.state === "busy")
			setState("idle");
	}

	/**
		* Starts an action that will affect the current call after a given time interval
		* Scheduling is async
		* @param  {Function} action The function to execute
		* @param  {Number} time   Time in MS to wait to execute the Function
		*/
	function start_timer(action, time) {
		asap(function () {
			clearTimeout(call_timer);
			call_timer = setTimeout(action, time);
		});
	}

	/**
		* Starts the timer for stopping a call after waiting for it to ring
		*/
	function start_call_timer() {
		start_timer(timeout_current_call, ring_time);
	}

	/**
		* Starts the timer for stopping a call after waiting too long to connect
		*/
	function start_connect_timer() {
		start_timer(timeout_current_call, CONNECT_TIME);
	}

	/**
		* Starts a timer for stopping the conference call if there was no answer for too long
		*/
	function start_conference_timer() {
		start_timer(timeout_conference_call, ring_time);
	}

	/**
		* Start a timer for stopping a conference call if connecting is taking too long
		*/
	function start_conference_connect_timer() {
		start_timer(timeout_conference_call, CONNECT_TIME);
	}

	/**
		* Detects whether a call shouldn't be timed out
		* @param  {Call}    call The call to check against
		* @return {Boolean}      Whether the call shouldn't be timed out or not
		*/
	function shouldnt_timeout_call(call) {
		return !call || call.state === "connected";
	}

	/**
		* Times out the current call, ending it with a reason of `Timed Out`
		*/
	function timeout_current_call() {
		var call = current_call;
		if (shouldnt_timeout_call(call)) return;

		if(call.state === "connecting" && first_connection) {
			first_connection = false;
			call.renegotiate("Retry Connection");
			start_connect_timer();
		} else if (call.initiator || (call.state === "connecting")) {
			return call.hangup("Timed Out").then(go_busy);
		} else {
			return call.deny("Timed Out");
		}
	}

	/**
		* Times out the current conference call, hanging up on it or denying it
		*/
	function timeout_conference_call() {
		var call = conference_call;
		if (shouldnt_timeout_call(call)) return;

		if (call.initiator || (call.state === "connecting"))
			return call.hangup("Timed Out");
		else return call.deny("Timed Out");
	}

	function handle_call_fail(e) {
		makeError("Call failed", e);
		go_idle();
	}

	/**
		* Creates a function for emitting errors
		* @param  {String}   prefix Something to prefix the error with when logging it
		* @return {Function}        The function to use for emitting errors
		*/
	function makeError(prefix) {
		return function (error) {
			console.debug(prefix, error);
			emit("error", error);
			logger("E", "Error: " + error);
		};
	}

	// TODO: add these back, m8
	function logger(type, msg) {
		var username = config.fname + " " + config.lname;
		dispatcher.emit("$mqtt:log/" + config.id + "/" + type, {
			logMsg: msg,
			logTransID: Buzz.room,
			logUserName: username
		});
	}

	function hide_video_view() {
		if (RTC.type !== "phonertc") return;
		var phonertc = window.cordova.plugins.phonertc;
		phonertc.hideVideoView();
	}

	function show_video_view() {
		if (RTC.type !== "phonertc") return;
		var phonertc = window.cordova.plugins.phonertc;
		phonertc.showVideoView();
	}

	function recalculate_video_view() {
		if (RTC.type !== "phonertc") return;
		RTC.resetVideoView();
	}

	function switch_camera(){
		if(current_call)
			current_call.switch("current").then(function(){
				if(conference_call) return conference_call.switch("conference");
				else return Promise.resolve();
			});
	}
}

function pass_trough(fn) {
	return function (data) {
		return Promise
			.resolve(data)
			.then(fn).then(function () {
				return data;
			});
	};
}
